import type {FormInterview} from 'src/types/crm';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useDispatch, useSelector} from 'react-redux';

import {debounce} from 'lodash';

import * as reduxApi from 'src/utils/redux-api-v2';

import {
  CollapsibleCard,
  CollapsibleCardContent,
} from '@spaced-out/ui-design-system/lib/components/CollapsibleCard';
import {
  BodyMedium,
  SubTitleMedium,
  SubTitleSmall,
  SubTitleLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Shimmer} from '@spaced-out/ui-design-system/lib/components/Shimmer';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  Toast,
  ToastTitle,
  TOAST_SEMANTIC,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import {Banner} from '@spaced-out/ui-design-system/lib/components/Banner';
import ViewCustomFields from 'src/components/crm/lib/custom-fields/view-custom-fields';
import {CustomFilter} from 'src/components/crm/applicants/body/tabComponents/forms/custom-form';

import {selectApplicantInformation} from 'src/selectors/crm';
import {getApplicantInformation} from 'src/action-creators/crm';
import css from './internal-response-form.css';


const CustomFormPanel = ({
  isCandidate,
  formInterview,
  isOpen,
  onClose,
  formType,
}: {
  isCandidate: boolean;
  formInterview: FormInterview;
  isOpen: boolean;
  onClose: () => void;
  formType: string;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const applicantDetails = useSelector(selectApplicantInformation);

  const [isCustomFieldLoading, setIsCustomFieldLoading] = React.useState(false);
  const [loadingStep, setLoadingStep] = React.useState(false);
  const [customForms, setCustomForms] = React.useState([]);
  const [updatedFields, setUpdatedFields] = React.useState([]);
  const [initialValues, setInitialValues] = React.useState([]);
  const [isValueChanged, setIsValueChanged] = React.useState(true);

  const formsSection =
    formType === 'CANDIDATE_FORM'
      ? formInterview.interviewCandidateFormField.customForms
      : formInterview.jobInternalForm?.customForms;
  const sections =
    formsSection && formsSection.length > 0 ? formsSection[0].sections : [];

  // @ts-expect-error - TS18048 - 'applicantDetails.applications' is possibly 'undefined'.
  const selectedApplicant = applicantDetails.applications.find(
    (applicant) =>
      applicant.jobId === formInterview.jobId &&
      applicant.candidateId === formInterview.candidateId,
  );

  React.useEffect(() => {
    const fetchPossibleValues = async () => {
      setIsCustomFieldLoading(true);
      const updatedSections = [...sections];

      const promises = updatedSections.map(async (section, sectionIndex) => {
        const fieldPromises = section.fields.map(async (field, fieldIndex) => {
          if (
            field.customField.fieldType === 'MULTI_SELECT' ||
            field.customField.fieldType === 'SINGLE_SELECT'
          ) {
            try {
              const response = await dispatch(
                reduxApi.get(
                  `api/custom-field/${field.customFieldId}/possible-values`,
                  {},
                  {
                    apiPath: '/trmapi/',
                  },
                ),
              );
              updatedSections[sectionIndex].fields[
                fieldIndex
              ].customField.possibleValues =
                response.length > 0 ? response[0].possibleValues : [];
            } catch (error: any) {
              console.log(error, 'error');
              updatedSections[sectionIndex].fields[
                fieldIndex
              ].customField.possibleValues = [];
            } finally {
              // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => { id: number; new: boolean; description?: string | undefined; createdBy: number; createdOn: number; updatedBy: number; updatedOn: number; organizationId: number; name: string; ... 14 more ...; value?: string | undefined; }[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
              setInitialValues((prev) => [
                ...prev,
                {
                  ...field.customField,
                },
              ]);
            }
          } else {
            // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => { id: number; new: boolean; description?: string | undefined; createdBy: number; createdOn: number; updatedBy: number; updatedOn: number; organizationId: number; name: string; ... 14 more ...; value?: string | undefined; }[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
            setInitialValues((prev) => [
              ...prev,
              {
                ...field.customField,
              },
            ]);
          }
        });
        await Promise.all(fieldPromises);
      });

      await Promise.all(promises);

      setCustomForms([
        // @ts-expect-error - TS2322 - Type '{ name: string; fields: JOB_CUSTOM_FIELD[]; active: true; createdBy: number; createdOn: number; filteredCustomFieldIds: never[]; filteredPlaceholderIds: never[]; formOf: string; ... 6 more ...; updatedOn: number; }' is not assignable to type 'never'.
        {
          name: 'Custom Form',
          fields: updatedSections.flatMap((section) => section.fields),
          active: true,
          createdBy: 0,
          createdOn: Date.now(),
          filteredCustomFieldIds: [],
          filteredPlaceholderIds: [],
          formOf: '',
          id: '',
          new: true,
          organizationId: 0,
          placeholderIds: [],
          referenceId: 0,
          updatedBy: 0,
          updatedOn: Date.now(),
        },
      ]);

      setIsCustomFieldLoading(false);
    };

    fetchPossibleValues();
  }, [formInterview, sections]);

  // @ts-expect-error - TS7031 - Binding element 'values' implicitly has an 'any' type. | TS7031 - Binding element 'isValid' implicitly has an 'any' type. | TS7031 - Binding element 'errors' implicitly has an 'any' type. | TS7031 - Binding element 'formId' implicitly has an 'any' type.
  const handleOnChange = ({values, isValid, errors, formId}) => {
    debounce(() => {
      setUpdatedFields(values);
      setIsValueChanged(isValid === undefined ? true : isValid);
    }, 300)();
  };

  const handleSave = () => {
    const formId =
      formType === 'CANDIDATE_FORM'
        ? Array.isArray(
            formInterview.interviewCandidateFormField.customForms,
          ) && formInterview.interviewCandidateFormField.customForms.length > 0
          ? formInterview.interviewCandidateFormField.customForms[0].id
          : 0
        : Array.isArray(formInterview.jobInternalForm?.customForms) &&
          formInterview.jobInternalForm.customForms.length > 0
        ? formInterview.jobInternalForm.customForms[0].id
        : 0;

    const stepCandidateId =
      formType === 'CANDIDATE_FORM'
        ? formInterview.interviewCandidateFormField.stepCandidateId
        : formInterview.jobInternalForm?.stepCandidateId;

    if (!formId || !stepCandidateId) {
      return;
    }
    const payload = ['INTERNAL_FORM', 'MANUAL_INTERNAL_FORM'].includes(formType)
      ? {
          formId,
          // @ts-expect-error - TS18048 - 'selectedApplicant' is possibly 'undefined'.
          entityId: selectedApplicant.id,
          isInternalFormNotManual: formType === 'INTERNAL_FORM',
          customFieldValues: updatedFields.map(({fieldId, value}) => ({
            fieldId,
            value,
          })),
        }
      : {
          formId,
          // @ts-expect-error - TS18048 - 'selectedApplicant' is possibly 'undefined'.
          entityId: selectedApplicant.id,
          customFieldValues: updatedFields.map(({fieldId, value}) => ({
            fieldId,
            value,
          })),
        };

    setLoadingStep(true);
    dispatch(
      reduxApi.post(
        `api/${
          formType === 'CANDIDATE_FORM'
            ? 'candidate-form'
            : 'internal-form/update'
        }/custom-field/value?interviewId=${stepCandidateId}`,
        payload,
        {},
        {
          apiPath: '/trmapi/',
        },
      ),
    )
      .then(() => {
        dispatch(
          getApplicantInformation(
            // @ts-expect-error - TS18048 - 'selectedApplicant' is possibly 'undefined'.
            selectedApplicant.candidateId,
            // @ts-expect-error - TS18048 - 'selectedApplicant' is possibly 'undefined'.
            selectedApplicant.jobId.toString(),
          ),
        );
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>
              {t('Form_updated_successfully', 'Form updated successfully')}
            </ToastTitle>
          </Toast>,
        );
        setLoadingStep(false);
        onClose();
      })
      .catch((error) => {
        console.log(error, 'error');
        setLoadingStep(false);
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>
              {t('Failed_to_update_form', 'Failed to update form')}
            </ToastTitle>
          </Toast>,
        );
      });
  };

  return (
    <Panel
      anchor="right"
      initialFocus={1}
      onClose={onClose}
      size="medium"
      tapOutsideToClose
      isOpen={isOpen}
      hideBackdrop={false}
    >
      <PanelHeader onCloseButtonClick={onClose} size="medium">
        <SubTitleLarge>
          {formType === 'CANDIDATE_FORM'
            ? t('Edit_Candidate_Form', 'Edit Candidate Form')
            : t('Edit_Internal_Form', 'Edit Internal Form')}
        </SubTitleLarge>
      </PanelHeader>
      <PanelBody className={css.panelBody}>
        {isCustomFieldLoading ? (
          <div className={css.circularLoaderContainer}>
            <CircularLoader />
          </div>
        ) : (
          <CustomFilter
            initialValues={initialValues || []}
            // @ts-expect-error - TS2769 - No overload matches this call.
            onChange={handleOnChange}
            customFormList={customForms || []}
            isLoadingCustomForm={false}
          />
        )}
      </PanelBody>
      <PanelFooter>
        <Button onClick={onClose} type="tertiary">
          {t('Close', 'Close')}
        </Button>
        <Button
          disabled={!isValueChanged || loadingStep || isCustomFieldLoading}
          onClick={handleSave}
          isLoading={loadingStep}
        >
          {t('Save', 'Save')}
        </Button>
      </PanelFooter>
    </Panel>
  );
};

export default CustomFormPanel;
